// Activities data for Rupee For Humanity
// Each activity represents an accomplishment/event by the organization

export const activitiesData = [
  {
    id: 1,
    slug: "digital-classroom-chikkaballapura-2024",
    title: "Transforming Education: Digital Classroom Initiative",
    subtitle: "Bringing Technology to Rural Classrooms in Chikkaballapura",
    date: "July 27, 2024",
    location: "Chikkaballapura District, Karnataka",
    heroImage: "/activities/activity1/image1.png",
    impactNumber: 180,
    impactLabel: "Children Empowered",
    
    // SEO optimized content
    metaTitle: "Digital Classroom Initiative in Chikkaballapura | Rupee For Humanity",
    metaDescription: "Rupee For Humanity transformed a government school in Chikkaballapura by providing books, stationery, laptops, and projectors to 180 underprivileged children, creating a digital learning environment.",
    
    // Story sections for emotional storytelling
    story: {
      hook: "In the heart of rural Karnataka, 180 young minds were waiting for an opportunity to dream bigger. On July 27th, 2024, Rupee For Humanity answered that call.",
      
      challenge: "Government schools in remote areas of Chikkaballapura district face a stark reality — limited resources, outdated teaching methods, and children who have never experienced digital learning. These bright young minds deserve the same opportunities as their urban counterparts.",
      
      solution: "Our team of dedicated volunteers traveled to Chikkaballapura with a mission: to bridge the digital divide and ignite a passion for learning. We didn't just bring supplies — we brought hope.",
      
      impact: "What started as a distribution drive became a transformation. A traditional classroom evolved into a digital learning hub, opening doors to endless possibilities for these children.",
    },
    
    // Key highlights with icons
    highlights: [
      {
        icon: "book",
        title: "Books & Stationery",
        description: "Brand new notebooks, textbooks, and complete stationery kits distributed to every child"
      },
      {
        icon: "laptop",
        title: "Digital Equipment",
        description: "Laptop and projector donated to transform the classroom into a digital learning space"
      },
      {
        icon: "school",
        title: "Digital Classroom Setup",
        description: "Complete installation and setup of digital classroom infrastructure"
      },
      {
        icon: "food",
        title: "Nutritious Meals",
        description: "Wholesome lunch and snacks served to all children and attendees"
      }
    ],
    
    // Image gallery with captions
    gallery: [
      {
        src: "/activities/activity1/image1.png",
        alt: "Children holding up their new books with joy",
        caption: "180 children proudly displaying their new books — each one a step towards a brighter future"
      },
      {
        src: "/activities/activity1/image3.png",
        alt: "Students seated with their educational materials",
        caption: "Eager young minds ready to embark on their educational journey"
      },
      {
        src: "/activities/activity1/image.png",
        alt: "Rupee For Humanity branded notebooks and stationery",
        caption: "Quality educational materials branded with hope and purpose"
      },
      {
        src: "/activities/activity1/image5.png",
        alt: "Volunteers distributing materials to children",
        caption: "Our volunteers personally handing over supplies, creating connections that matter"
      },
      {
        src: "/activities/activity1/image4.png",
        alt: "Volunteers preparing meals for distribution",
        caption: "Love served on a plate — our team preparing nutritious meals for the children"
      },
      {
        src: "/activities/activity1/image2.png",
        alt: "Laptop and projector donation",
        caption: "The gateway to digital learning — laptop and projector ready to transform education"
      },
      {
        src: "/activities/activity1/image8.png",
        alt: "Digital classroom setup with projector",
        caption: "From traditional to digital — the classroom transformation in action"
      },
      {
        src: "/activities/activity1/image6.png",
        alt: "Team photo with school officials",
        caption: "Partnership in progress — our team with school officials committed to change"
      },
      {
        src: "/activities/activity1/image7.png",
        alt: "Full volunteer team photo",
        caption: "The hearts behind the mission — our dedicated volunteer team"
      }
    ],
    
    // Quote/testimonial
    quote: {
      text: "Education is the most powerful weapon which you can use to change the world.",
      author: "Nelson Mandela"
    },
    
    // Call to action
    cta: {
      title: "Be Part of the Change",
      description: "Your contribution can help us reach more schools and transform more lives. Every rupee counts.",
      buttonText: "Donate Now",
      buttonLink: "/?donate=true"
    },
    
    // Tags for filtering/SEO
    tags: ["education", "digital classroom", "books distribution", "rural development", "children", "Karnataka"]
  },
  {
    id: 2,
    slug: "education-sponsorship-2024",
    title: "Sponsoring Dreams: Education for Underprivileged Children",
    subtitle: "Giving Wings to Young Aspirations Through Direct School Sponsorships",
    date: "July 23, 2024",
    location: "Bengaluru, Karnataka",
    heroImage: "/activities/activity2/image.png",
    impactNumber: 4,
    impactLabel: "Children's Futures Secured",
    
    metaTitle: "Education Sponsorship for Underprivileged Children 2024 | Rupee For Humanity",
    metaDescription: "Rupee For Humanity sponsored education for 4 underprivileged children from daily wage laborer families in Bengaluru, ensuring transparent direct-to-school payments.",
    
    story: {
      hook: "For children of daily wage laborers, education often remains a distant dream. On July 23rd, 2024, Rupee For Humanity turned that dream into reality for four deserving children.",
      
      challenge: "In the bustling city of Bengaluru, countless families struggle to make ends meet. Parents working as daily wage laborers earn barely enough for food and shelter — school fees remain an impossible burden. Their children, bright and eager to learn, watch their peers go to school while they stay home.",
      
      solution: "After thorough background verification, RFH identified four children from families who genuinely needed support. We didn't just hand over money — we ensured complete transparency by writing cheques directly to schools, guaranteeing every rupee goes toward education.",
      
      impact: "Four children now wear school uniforms with pride. Four families have hope for a better tomorrow. This is the power of direct, transparent giving — changing lives one child at a time.",
    },
    
    highlights: [
      {
        icon: "school",
        title: "Direct School Payments",
        description: "All fees paid directly to schools via cheque for complete transparency"
      },
      {
        icon: "book",
        title: "Full Academic Year",
        description: "Complete sponsorship covering the entire 2024 academic year"
      },
      {
        icon: "heart",
        title: "Background Verified",
        description: "Thorough verification to ensure support reaches the most deserving families"
      },
      {
        icon: "users",
        title: "Family Support",
        description: "Supporting children of daily wage laborers who cannot afford education"
      }
    ],
    
    gallery: [
      {
        src: "/activities/activity2/image.png",
        alt: "RFH volunteer presenting education sponsorship cheque",
        caption: "A moment of hope — presenting the sponsorship cheque that opens doors to education"
      },
      {
        src: "/activities/activity2/image copy.png",
        alt: "Education sponsorship ceremony",
        caption: "Every cheque represents a child's future secured"
      },
      {
        src: "/activities/activity2/image copy 2.png",
        alt: "Sponsored child with family",
        caption: "Smiles that make every effort worthwhile"
      }
    ],
    
    quote: {
      text: "Education is the passport to the future, for tomorrow belongs to those who prepare for it today.",
      author: "Malcolm X"
    },
    
    cta: {
      title: "Sponsor a Child's Education",
      description: "₹15,000 can sponsor a child's education for an entire year. Your contribution goes directly to schools.",
      buttonText: "Donate Now",
      buttonLink: "/?donate=true"
    },
    
    tags: ["education", "sponsorship", "underprivileged children", "transparency", "Bengaluru", "school fees"]
  },
  {
    id: 3,
    slug: "footwear-recycling-greensole-2024",
    title: "Stepping Towards Sustainability: Footwear Recycling Drive",
    subtitle: "300 Pairs of Shoes Given New Life Through Greensole Partnership",
    date: "June 5, 2024",
    location: "Bengaluru, Karnataka",
    heroImage: "/activities/activity3/image1.png",
    impactNumber: 300,
    impactLabel: "Footwear Recycled",
    
    metaTitle: "Footwear Recycling Drive with Greensole Foundation | Rupee For Humanity",
    metaDescription: "Rupee For Humanity collected 300 pairs of old footwear across Bengaluru, partnering with Greensole Foundation to refurbish and donate them to the needy while protecting the environment.",
    
    story: {
      hook: "What happens to your old shoes? Most end up in landfills, releasing toxins as they burn. On World Environment Day 2024, Rupee For Humanity chose a different path — one that protects our planet while helping those in need.",
      
      challenge: "Every year, millions of shoes are discarded, contributing to environmental pollution. When burned, they release harmful chemicals that damage our ozone layer. Meanwhile, countless underprivileged individuals walk barefoot or with worn-out footwear, risking injury and infection.",
      
      solution: "We launched an ambitious collection drive across Bengaluru, gathering 300 pairs of old footwear from generous donors. Partnering with Greensole Foundation — experts in footwear refurbishment — we ensured these shoes would walk again, this time on the feet of those who need them most.",
      
      impact: "300 pairs of shoes saved from landfills. 300 pairs refurbished and donated to the needy. One initiative, double the impact — for the environment and for humanity. Greensole Foundation recognized our efforts with a certificate of appreciation.",
    },
    
    highlights: [
      {
        icon: "recycle",
        title: "300 Footwear Collected",
        description: "Massive collection drive across Bengaluru neighborhoods"
      },
      {
        icon: "handshake",
        title: "Greensole Partnership",
        description: "Collaborated with experts who refurbish and donate footwear"
      },
      {
        icon: "leaf",
        title: "Environmental Impact",
        description: "Prevented toxic pollution from footwear burning in landfills"
      },
      {
        icon: "award",
        title: "Recognition Received",
        description: "Appreciation certificate from Greensole Foundation"
      }
    ],
    
    gallery: [
      {
        src: "/activities/activity3/image1.png",
        alt: "Collected footwear packed for recycling",
        caption: "300 pairs of hope — packed and ready for their journey to new owners"
      },
      {
        src: "/activities/activity3/image2.png",
        alt: "Footwear collection process",
        caption: "Every pair collected is a step towards a greener planet"
      },
      {
        src: "/activities/activity3/image3.png",
        alt: "Partnership with Greensole Foundation",
        caption: "Collaboration that multiplies impact — with Greensole Foundation"
      }
    ],
    
    quote: {
      text: "We do not inherit the earth from our ancestors; we borrow it from our children.",
      author: "Native American Proverb"
    },
    
    cta: {
      title: "Support Our Green Initiatives",
      description: "Help us continue protecting the environment while serving humanity. Every contribution counts.",
      buttonText: "Donate Now",
      buttonLink: "/?donate=true"
    },
    
    tags: ["environment", "recycling", "sustainability", "footwear", "Greensole", "World Environment Day", "Bengaluru"]
  },
  {
    id: 4,
    slug: "rfh-juniors-run-2024",
    title: "RFH Juniors Run 2024: Young Feet, Big Hearts",
    subtitle: "Bengaluru's Children Run to Fund Education for the Underprivileged",
    date: "February 11, 2024",
    location: "Dayananda Sagar College of Engineering, Bengaluru",
    heroImage: "/activities/activity4/image.png",
    impactNumber: 100,
    impactLabel: "Young Runners",
    
    metaTitle: "RFH Juniors Run 2024 - Marathon for Education | Rupee For Humanity",
    metaDescription: "Rupee For Humanity's inaugural Juniors Run 2024 at Dayananda Sagar College, Bengaluru brought together young runners to raise funds for underprivileged children's education.",
    
    story: {
      hook: "What if children could help other children? On February 11th, 2024, young runners across Bengaluru proved that age is no barrier to making a difference. Welcome to RFH Juniors Run — where every step counts.",
      
      challenge: "Fundraising for education is an ongoing challenge. Traditional methods often fail to engage the younger generation. We asked ourselves: How can we inspire children to become changemakers while promoting fitness and community spirit?",
      
      solution: "RFH Juniors Run 2024 was born — our first-ever marathon event exclusively for children. Hosted at the prestigious Dayananda Sagar College of Engineering, young participants ran not just for medals, but for a cause. Every registration, every step raised funds for underprivileged children's education.",
      
      impact: "The energy was electric. Children ran with purpose, parents cheered with pride, and together we raised funds that directly sponsored education for underprivileged kids in Bengaluru. This wasn't just a run — it was a movement.",
    },
    
    highlights: [
      {
        icon: "running",
        title: "Inaugural Event",
        description: "First-ever RFH Juniors Run bringing together young changemakers"
      },
      {
        icon: "school",
        title: "Education Funded",
        description: "All proceeds sponsored underprivileged children's education"
      },
      {
        icon: "building",
        title: "Premium Venue",
        description: "Hosted at Dayananda Sagar College of Engineering"
      },
      {
        icon: "users",
        title: "Community Spirit",
        description: "Families came together for fitness and philanthropy"
      }
    ],
    
    gallery: [
      {
        src: "/activities/activity4/image.png",
        alt: "Children running in RFH Juniors Run 2024",
        caption: "Young feet pounding the track — running for those who can't afford school"
      },
      {
        src: "/activities/activity4/image1.png",
        alt: "Participants at the starting line",
        caption: "Ready, set, change the world!"
      },
      {
        src: "/activities/activity4/image2.png",
        alt: "Kids crossing finish line",
        caption: "Every finisher is a winner — and a changemaker"
      },
      {
        src: "/activities/activity4/image3.png",
        alt: "Medal ceremony",
        caption: "Medals earned, lives changed"
      },
      {
        src: "/activities/activity4/image4.png",
        alt: "Group photo of participants",
        caption: "The faces of change — our incredible young runners"
      },
      {
        src: "/activities/activity4/image5.png",
        alt: "Event volunteers and organizers",
        caption: "The team that made it all possible"
      },
      {
        src: "/activities/activity4/image6.png",
        alt: "Families at the event",
        caption: "Families united for a cause greater than themselves"
      }
    ],
    
    quote: {
      text: "The future belongs to those who believe in the beauty of their dreams.",
      author: "Eleanor Roosevelt"
    },
    
    cta: {
      title: "Join Our Next Event",
      description: "Be part of RFH's mission. Participate, volunteer, or donate — every action creates ripples of change.",
      buttonText: "Donate Now",
      buttonLink: "/?donate=true"
    },
    
    tags: ["marathon", "juniors run", "fundraising", "education", "children", "Bengaluru", "fitness", "community"]
  },
  {
    id: 5,
    slug: "volunteers-meet-2023-24",
    title: "Volunteers Meet 2023-24: Planning for Impact",
    subtitle: "Where Passionate Hearts Unite to Chart the Course for Change",
    date: "December 9, 2023",
    location: "Bengaluru, Karnataka",
    heroImage: "/activities/activity5/image.png",
    impactNumber: 15,
    impactLabel: "Dedicated Volunteers",
    
    metaTitle: "RFH Volunteers Annual Meet 2023-24 | Rupee For Humanity",
    metaDescription: "Rupee For Humanity volunteers gathered for their annual planning meet, self-sponsoring all expenses while strategizing impactful initiatives for the upcoming year.",
    
    story: {
      hook: "Behind every successful initiative is a team of dedicated individuals who give their time, energy, and hearts without expecting anything in return. On December 9th, 2023, these unsung heroes came together with one purpose — to plan how they could serve society better.",
      
      challenge: "Running a nonprofit requires more than good intentions. It demands strategic planning, coordination, and a shared vision. How do you align diverse volunteers with different skills and schedules toward common goals while maintaining the integrity and transparency that defines your organization?",
      
      solution: "Our annual volunteers meet brought together the backbone of RFH — our dedicated volunteers. In true RFH spirit, every expense was self-sponsored by the volunteers themselves. No donor money was used for this gathering, maintaining the dignity and transparency that our organization stands for. Over meaningful discussions and shared meals, we mapped out the events and initiatives for the coming year.",
      
      impact: "The meeting resulted in a comprehensive roadmap of activities for 2024, including education sponsorships, environmental initiatives, and community outreach programs. More importantly, it strengthened the bonds between volunteers who share a common dream — a better India for all.",
    },
    
    highlights: [
      {
        icon: "users",
        title: "Self-Sponsored Event",
        description: "All food and expenses covered by volunteers themselves — zero donor funds used"
      },
      {
        icon: "clipboard",
        title: "Strategic Planning",
        description: "Comprehensive roadmap created for upcoming year's initiatives"
      },
      {
        icon: "handshake",
        title: "Team Bonding",
        description: "Strengthening connections between volunteers who drive our mission"
      },
      {
        icon: "lightbulb",
        title: "Fresh Ideas",
        description: "Brainstorming new ways to maximize impact in communities"
      }
    ],
    
    gallery: [
      {
        src: "/activities/activity5/image.png",
        alt: "RFH volunteers at annual meet",
        caption: "The hearts behind the mission — our volunteer family planning for impact"
      },
      {
        src: "/activities/activity5/image1.png",
        alt: "Volunteers in discussion",
        caption: "Ideas flowing, plans forming — this is where change begins"
      }
    ],
    
    quote: {
      text: "Alone we can do so little; together we can do so much.",
      author: "Helen Keller"
    },
    
    cta: {
      title: "Join Our Volunteer Family",
      description: "Be part of a team that's making real change. Your time and skills can transform lives.",
      buttonText: "Become a Volunteer",
      buttonLink: "/volunteer-register"
    },
    
    tags: ["volunteers", "planning", "team", "transparency", "NGO", "Bengaluru"]
  },
  {
    id: 6,
    slug: "childrens-day-chintamani-2023",
    title: "Children's Day Celebration: A Day of Joy in Chintamani",
    subtitle: "300 Government School Children Experience a Day They'll Never Forget",
    date: "November 18, 2023",
    location: "Batlahalli, Chintamani, Karnataka",
    heroImage: "/activities/activity6/image.png",
    impactNumber: 300,
    impactLabel: "Children Celebrated",
    
    metaTitle: "Children's Day Celebration at Chintamani Government School | Rupee For Humanity",
    metaDescription: "Rupee For Humanity celebrated Children's Day with 300 government school students in Chintamani, featuring sports, education materials, hygiene awareness, and more. Featured in Praja Vani newspaper.",
    
    story: {
      hook: "Children's Day should be special for every child, not just those in privileged schools. On November 18th, 2023, Rupee For Humanity brought the celebration to 300 children in a government school in rural Chintamani — and the joy was immeasurable.",
      
      challenge: "In rural Karnataka, government school children often miss out on the celebrations that urban kids take for granted. Children's Day passes like any other day — no special events, no gifts, no memories to cherish. These children deserve to feel special too.",
      
      solution: "RFH organized a comprehensive Children's Day celebration that went far beyond just fun and games. We combined celebration with education, distributing books and stationery, conducting awareness sessions on waste segregation and menstrual hygiene, organizing sports events, and providing sports equipment. The day was filled with learning, laughter, and lasting memories.",
      
      impact: "300 children experienced a Children's Day they'll remember forever. They received educational materials, learned important life skills, played sports with proper equipment, and most importantly, felt valued and celebrated. The event was recognized and covered by Praja Vani, one of Karnataka's leading newspapers, amplifying our message of inclusive celebration.",
    },
    
    highlights: [
      {
        icon: "trophy",
        title: "Sports Day Events",
        description: "Organized competitive sports activities with prizes for winners"
      },
      {
        icon: "book",
        title: "Education Materials",
        description: "Books, stationery, and library donations for continued learning"
      },
      {
        icon: "heartPulse",
        title: "Health Awareness",
        description: "Sessions on waste segregation and menstrual hygiene education"
      },
      {
        icon: "newspaper",
        title: "Media Recognition",
        description: "Event covered by Praja Vani newspaper, spreading awareness"
      }
    ],
    
    gallery: [
      {
        src: "/activities/activity6/image.png",
        alt: "Children's Day celebration at Chintamani",
        caption: "300 smiles that made every effort worthwhile"
      },
      {
        src: "/activities/activity6/image1.png",
        alt: "Sports activities during celebration",
        caption: "Sports day — where every child is a champion"
      },
      {
        src: "/activities/activity6/image2.png",
        alt: "Book distribution to students",
        caption: "Books that open doors to new worlds"
      },
      {
        src: "/activities/activity6/image3.png",
        alt: "Awareness session with students",
        caption: "Learning life skills that last a lifetime"
      },
      {
        src: "/activities/activity6/image4.png",
        alt: "Lunch distribution",
        caption: "Sharing meals, sharing joy"
      },
      {
        src: "/activities/activity6/image5.png",
        alt: "Sports kit distribution",
        caption: "Equipment to fuel their sporting dreams"
      },
      {
        src: "/activities/activity6/image6.png",
        alt: "Library book donation",
        caption: "Building a library, building futures"
      },
      {
        src: "/activities/activity6/image7.png",
        alt: "Group activities",
        caption: "Together in celebration, together in growth"
      },
      {
        src: "/activities/activity6/image8.png",
        alt: "Praja Vani newspaper coverage",
        caption: "Our story reaches thousands through Praja Vani"
      }
    ],
    
    quote: {
      text: "Children are the world's most valuable resource and its best hope for the future.",
      author: "John F. Kennedy"
    },
    
    cta: {
      title: "Sponsor a Celebration",
      description: "Help us bring joy to more children. Your donation can make their special days truly special.",
      buttonText: "Donate Now",
      buttonLink: "/?donate=true"
    },
    
    tags: ["children's day", "celebration", "education", "sports", "hygiene awareness", "Chintamani", "rural Karnataka", "media coverage"]
  },
  {
    id: 7,
    slug: "manava-sarathi-ngo-visit-2023",
    title: "Bringing Hope to Manava Sarathi: Supporting Orphaned Children",
    subtitle: "A Day of Care, Repairs, and Renewed Hope for 85 Children",
    date: "October 21, 2023",
    location: "Manava Sarathi NGO, Karnataka",
    heroImage: "/activities/activity7/image.png",
    impactNumber: 85,
    impactLabel: "Children Supported",
    
    metaTitle: "Visit to Manava Sarathi NGO - Supporting Orphaned Children | Rupee For Humanity",
    metaDescription: "Rupee For Humanity visited Manava Sarathi NGO to support 85 orphaned children with educational materials, sports equipment, food, electrical repairs, and organized games.",
    
    story: {
      hook: "Some children don't have families to come home to. They live in orphanages, cared for by NGOs that struggle with limited resources. On October 21st, 2023, Rupee For Humanity visited Manava Sarathi NGO to show these 85 children that they are not forgotten.",
      
      challenge: "Orphanages face unique challenges — they need not just emotional support for children, but also practical help with infrastructure, educational materials, and recreational facilities. Manava Sarathi NGO was struggling with basic electrical issues that affected the children's daily lives, alongside needs for educational and sports materials.",
      
      solution: "RFH took a holistic approach to support. We didn't just visit with gifts — we assessed what the NGO truly needed. Our team distributed books and stationery for education, sports equipment like carrom boards and volleyballs for recreation, prepared and served delicious meals, and even repaired the NGO's electrical systems. We organized games with prizes, giving children a day filled with joy and excitement.",
      
      impact: "85 children received educational materials to support their studies, sports equipment to encourage physical activity, and most importantly, a day of undivided attention and care. The electrical repairs improved their daily living conditions. But beyond the tangible gifts, we gave them something priceless — the knowledge that people care about their wellbeing.",
    },
    
    highlights: [
      {
        icon: "book",
        title: "Educational Support",
        description: "Books and stationery distributed to support children's studies"
      },
      {
        icon: "volleyball",
        title: "Sports Equipment",
        description: "Carrom boards, volleyballs, and more for healthy recreation"
      },
      {
        icon: "bolt",
        title: "Infrastructure Repair",
        description: "Electrical systems repaired to improve daily living conditions"
      },
      {
        icon: "gamepad",
        title: "Fun & Games",
        description: "Organized games with prizes bringing joy to every child"
      }
    ],
    
    gallery: [
      {
        src: "/activities/activity7/image.png",
        alt: "RFH team at Manava Sarathi NGO",
        caption: "Bringing hope and support to Manava Sarathi's children"
      },
      {
        src: "/activities/activity7/image1.png",
        alt: "Book distribution to orphaned children",
        caption: "Every book is a window to new possibilities"
      },
      {
        src: "/activities/activity7/image2.png",
        alt: "Food preparation and distribution",
        caption: "Preparing meals with love for 85 hungry hearts"
      },
      {
        src: "/activities/activity7/image3.png",
        alt: "Sports equipment donation",
        caption: "Play is essential — sports gear for healthy childhoods"
      },
      {
        src: "/activities/activity7/image4.png",
        alt: "Electrical repair work",
        caption: "Fixing what's broken, lighting up lives"
      },
      {
        src: "/activities/activity7/image5.png",
        alt: "Games and activities with children",
        caption: "Laughter echoes — games that create memories"
      },
      {
        src: "/activities/activity7/image6.png",
        alt: "Prize distribution",
        caption: "Every child is a winner in our eyes"
      }
    ],
    
    quote: {
      text: "The best way to find yourself is to lose yourself in the service of others.",
      author: "Mahatma Gandhi"
    },
    
    cta: {
      title: "Support Orphaned Children",
      description: "These children need ongoing support. Your donation helps us continue visiting and supporting orphanages.",
      buttonText: "Donate Now",
      buttonLink: "/?donate=true"
    },
    
    tags: ["orphanage", "NGO support", "children", "education", "sports", "infrastructure", "community service"]
  },
  {
    id: 8,
    slug: "sports-jersey-distribution-2023",
    title: "Championing Dreams: Sports Jersey Distribution",
    subtitle: "Equipping Young Athletes Who Reached District Level Competition",
    date: "September 18, 2023",
    location: "Karnataka",
    heroImage: "/activities/activity8/image.png",
    impactNumber: 20,
    impactLabel: "Young Athletes Supported",
    
    metaTitle: "Sports Jersey Distribution for Government School Athletes | Rupee For Humanity",
    metaDescription: "Rupee For Humanity donated sports jerseys and shorts to talented girl athletes from government schools who qualified for district level competitions.",
    
    story: {
      hook: "Talent doesn't discriminate based on economic background, but opportunities often do. When young girls from government schools qualified for district level sports competitions, they had the skill but lacked proper sportswear. Rupee For Humanity stepped in to ensure their talent wouldn't be held back.",
      
      challenge: "Government school students often possess remarkable athletic talent, but lack the resources to compete professionally. These girls had trained hard and earned their place in district level competitions, yet they didn't have proper sports jerseys and shorts — basic equipment that their competitors from private schools take for granted.",
      
      solution: "RFH identified these deserving young athletes and provided them with quality sports jerseys and shorts. But this wasn't just about clothing — it was about dignity, confidence, and showing these girls that their achievements matter. When they step onto the field in proper sportswear, they compete as equals.",
      
      impact: "These young athletes now have the proper gear to compete with confidence at district level. More than the jerseys, they received validation that their hard work is recognized and supported. This small step helps them dream bigger — perhaps state level, national level, or even beyond. Every champion starts somewhere, and we're proud to support their journey.",
    },
    
    highlights: [
      {
        icon: "shirt",
        title: "Quality Sportswear",
        description: "Professional jerseys and shorts for competitive sports"
      },
      {
        icon: "medal",
        title: "District Level Athletes",
        description: "Supporting girls who qualified through merit and hard work"
      },
      {
        icon: "female",
        title: "Empowering Girls",
        description: "Breaking barriers for female athletes in rural areas"
      },
      {
        icon: "star",
        title: "Building Confidence",
        description: "Proper gear means competing with dignity and self-belief"
      }
    ],
    
    gallery: [
      {
        src: "/activities/activity8/image.png",
        alt: "Sports jersey distribution ceremony",
        caption: "Equipping champions — jersey distribution to district level athletes"
      },
      {
        src: "/activities/activity8/image1.png",
        alt: "Young athletes with new jerseys",
        caption: "Pride in their eyes — ready to compete with confidence"
      },
      {
        src: "/activities/activity8/image2.png",
        alt: "Group photo with athletes",
        caption: "Future champions supported by RFH"
      }
    ],
    
    quote: {
      text: "Sports teaches you character, it teaches you to play by the rules, it teaches you to know what it feels like to win and lose.",
      author: "Billie Jean King"
    },
    
    cta: {
      title: "Support Young Athletes",
      description: "Help us equip more talented students with the gear they need to compete and succeed.",
      buttonText: "Donate Now",
      buttonLink: "/?donate=true"
    },
    
    tags: ["sports", "jersey distribution", "girls empowerment", "athletics", "district competition", "government school"]
  }
];

export const getActivityBySlug = (slug) => {
  return activitiesData.find(activity => activity.slug === slug);
};

export const getActivityById = (id) => {
  return activitiesData.find(activity => activity.id === parseInt(id));
};

export default activitiesData;
